import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/agents/log-triage/learnings` — admin-only review queue for what the log triage
 * learned from people's own handling of its tickets.
 *
 * A signature whose tickets were deleted, or closed without anyone booking a
 * minute on them, is proposed as a rule. It does nothing until Activated;
 * Dismiss files that signature again from the next sweep on, ten minutes
 * later. See `server/services/log-triage/learning.ts`.
 */

type Learning = {
  id: string;
  signatureHash: string;
  sample: string;
  container: string;
  action: "drop" | "low";
  status: "draft" | "active" | "dismissed";
  evidenceCount: number;
  rationale: string;
  appliedCount: number;
  lastAppliedAt: string | null;
  generatedAt: string;
  project: { id: string; name: string } | null;
  reviewedBy: { id: string; name: string | null } | null;
  reviewedAt: string | null;
};

const TABS = ["draft", "active", "expired", "dismissed", "all"] as const;
type Tab = (typeof TABS)[number];

export function TriageLearningsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mining, setMining] = useState(false);
  const [mineMessage, setMineMessage] = useState<string | null>(null);

  const load = useCallback(
    (which: Tab) => {
      setRows(null);
      return api
        .get<{ learnings: Learning[] }>(
          `/api/log-triage-learnings${which === "all" ? "" : `?status=${which}`}`,
        )
        .then((res) => setRows(res.learnings))
        .catch(() => setError(t("agentTabs.triageLearnings.loadFailed")));
    },
    [t],
  );

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  async function patch(id: string, body: Record<string, string>, failure: string) {
    setBusyId(id);
    try {
      await api.patch(`/api/log-triage-learnings/${id}`, body);
      await load(tab);
    } catch {
      setError(failure);
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMining(true);
    setMineMessage(null);
    try {
      await api.post("/api/log-triage-learnings/mine", {});
      setMineMessage(t("agentTabs.triageLearnings.mining"));
    } catch {
      setMineMessage(t("agentTabs.triageLearnings.miningFailed"));
    } finally {
      setMining(false);
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t("agentTabs.triageLearnings.title")}</h2>
          <p className="text-sm text-muted-foreground">{t("agentTabs.triageLearnings.intro")}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void mineNow()}
            disabled={mining}
          >
            {mining
              ? t("agentTabs.triageLearnings.starting")
              : t("agentTabs.triageLearnings.mineNow")}
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`triage-learnings-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(`agentTabs.triageLearnings.tabs.${key}`)}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="triage-learnings-empty">
          {tab === "draft"
            ? t("agentTabs.triageLearnings.emptyDraft")
            : t("agentTabs.triageLearnings.empty")}
        </p>
      )}

      <div className="space-y-3">
        {rows?.map((r) => (
          <Card key={r.id} data-testid="triage-learning-card">
            <CardContent className="space-y-2 p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline">
                  {r.project?.name ?? t("agentTabs.triageLearnings.allProjects")}
                </Badge>
                <span className="font-mono">{r.container}</span>
                <span>
                  · {t("agentTabs.triageLearnings.thrownAway", { count: r.evidenceCount })}
                </span>
                <span>· {t(`agentTabs.triageLearnings.action.${r.action}`)}</span>
                {r.appliedCount > 0 && (
                  <span>· {t("agentTabs.triageLearnings.applied", { count: r.appliedCount })}</span>
                )}
                {r.reviewedBy && (
                  <span>
                    ·{" "}
                    {t(
                      r.status === "active"
                        ? "agentTabs.triageLearnings.activatedBy"
                        : "agentTabs.triageLearnings.dismissedBy",
                      { name: r.reviewedBy.name ?? t("agentTabs.triageLearnings.someone") },
                    )}
                  </span>
                )}
              </div>
              <p className="font-mono text-xs break-all">{r.sample}</p>
              <p className="text-sm">{r.rationale}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {r.status !== "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    data-testid="activate-triage-learning"
                    onClick={() =>
                      void patch(
                        r.id,
                        { status: "active" },
                        t("agentTabs.triageLearnings.saveDecisionFailed"),
                      )
                    }
                  >
                    {t("agentTabs.triageLearnings.activate")}
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    data-testid="dismiss-triage-learning"
                    onClick={() =>
                      void patch(
                        r.id,
                        { status: "dismissed" },
                        t("agentTabs.triageLearnings.saveDecisionFailed"),
                      )
                    }
                  >
                    {t("agentTabs.triageLearnings.dismiss")}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() =>
                    void patch(
                      r.id,
                      { action: r.action === "drop" ? "low" : "drop" },
                      t("agentTabs.triageLearnings.saveChangeFailed"),
                    )
                  }
                >
                  {r.action === "drop"
                    ? t("agentTabs.triageLearnings.lowInstead")
                    : t("agentTabs.triageLearnings.dropInstead")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
